import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Globe, Plus, Trash } from '@phosphor-icons/react';
import TeamPanel from '../TeamPanel';
import { PLATFORM_ICONS } from './constants';
import ContextHelpButton from '../ContextHelpButton';

// PRODUCT-IA-CLEANUP-001A: extracted from the historical `social` tab
// inline JSX in ProjectView.js — this is the Social contextual group
// inside the Connections workspace. Self-contained (own load of
// `/social/platforms` and `/social/project/{id}`), reusing the exact
// same endpoints/OAuth flow as before.
export default function SocialConnectionsSection({ api, project }) {
  const { t } = useTranslation();
  const [socialProfiles, setSocialProfiles] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddManual, setShowAddManual] = useState(null);
  const [manualName, setManualName] = useState('');
  const [connectingPlatformId, setConnectingPlatformId] = useState(null);
  const [removingProfileId, setRemovingProfileId] = useState(null);

  useEffect(() => {
    if (!project?.id) return;
    setLoading(true);
    Promise.all([
      api.get('/social/platforms').then(r => setPlatforms(r.data)).catch(() => {}),
      api.get(`/social/project/${project.id}`).then(r => setSocialProfiles(r.data || [])).catch(() => setSocialProfiles([])),
    ]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  const connectSocial = async (platform) => {
    setConnectingPlatformId(platform.id);
    const tid = toast.loading(t('project.social.connecting', { platform: platform.name }));
    try {
      const { data } = await api.get(`/social/oauth/start/${platform.id}?project_id=${encodeURIComponent(project.id)}`);
      const popup = window.open(data.auth_url, 'oauth_popup', 'width=600,height=700,left=200,top=100');
      const handler = (e) => {
        if (e.data?.type === 'oauth_success' && e.data?.project_id === project.id) {
          window.removeEventListener('message', handler);
          popup?.close();
          api.get(`/social/project/${project.id}`).then(r => setSocialProfiles(r.data || [])).catch(() => {});
          toast.success(t('project.social.connectedSuccess', { name: e.data.name }), { id: tid });
          setConnectingPlatformId(null);
        } else if (e.data?.type === 'oauth_error') {
          window.removeEventListener('message', handler);
          popup?.close();
          toast.error(t('project.social.connectionError', { error: e.data.error }), { id: tid });
          setConnectingPlatformId(null);
        }
      };
      window.addEventListener('message', handler);
    } catch (e) {
      toast.error(`${t('common.error')}: ${e.response?.data?.detail || e.message}`, { id: tid });
      setConnectingPlatformId(null);
    }
  };

  const addManualProfile = async (platformId) => {
    if (!manualName.trim()) return;
    const tid = toast.loading(t('project.social.addingProfile'));
    try {
      const { data } = await api.post('/social/profiles', {
        project_id: project.id,
        platform: platformId,
        profile_name: manualName,
        connection_mode: 'manual'
      });
      setSocialProfiles(prev => [...prev, data]); setShowAddManual(null); setManualName('');
      toast.success(t('project.social.profileAdded'), { id: tid });
    } catch (e) {
      // FREE-PLAN-WALLET-PUBLISHING-001A / part E: social_profile_limit_reached
      // carries a structured {code, message, ...} detail, not a plain string —
      // surface it instead of the generic hardcoded error.
      const detail = e.response?.data?.detail;
      const message = detail && typeof detail === 'object' ? detail.message : detail;
      toast.error(message || t('project.social.addProfileError'), { id: tid });
    }
  };

  const removeSocialProfile = async (id) => {
    setRemovingProfileId(id);
    const tid = toast.loading(t('project.social.removingProfile'));
    try {
      await api.delete(`/social/profiles/${id}?project_id=${encodeURIComponent(project.id)}`);
      setSocialProfiles(prev => prev.filter(p => p.id !== id));
      toast.success(t('project.social.profileRemoved'), { id: tid });
    } catch (e) { toast.error(t('project.social.removeProfileError'), { id: tid }); }
    setRemovingProfileId(null);
  };

  if (loading) {
    return <p className="text-sm text-[var(--text-muted)] py-8">{t('common.loading')}</p>;
  }

  return (
    <div className="pt-2 pb-10">
      <div className="mb-2 flex justify-end"><ContextHelpButton articleId="social-connections" label="Guida connessioni social" /></div>
      <div
        className="mb-4 rounded-xl px-4 py-3 text-xs"
        style={{
          background: 'rgba(45, 212, 191, 0.08)',
          border: '1px solid rgba(45, 212, 191, 0.22)',
          color: 'var(--text-secondary)'
        }}
      >
        {t('project.social.projectIsolationNotice')}
      </div>
      {/* Social publishing platforms */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 mb-6">
        {platforms.filter(p => !p.is_tool).map(platform => {
          const pi = PLATFORM_ICONS[platform.id] || { Icon: Globe, color: '#fff' };
          const connected = socialProfiles.filter(p => p.platform === platform.id);
          return (
            <div key={platform.id} className="card" data-testid={`social-platform-${platform.id}`}>
              <div className="flex items-center gap-3 mb-3">
                <pi.Icon weight="fill" size={22} color={pi.color} />
                <div>
                  <p className="font-semibold text-sm">{platform.name}</p>
                  <p className="text-[10px] text-[var(--text-muted)]">{connected.length} {t('project.social.connected')}</p>
                </div>
              </div>
              {connected.map(prof => (
                <div key={prof.id} className="flex items-center justify-between py-1.5 px-2 rounded mb-1" style={{ background: 'rgba(34,197,94,0.1)' }}>
                  <span className="text-xs truncate">{prof.profile_name}</span>
                  <div className="flex gap-1 flex-shrink-0">
                    <span className="badge green text-[8px]">{t('common.active')}</span>
                    <button className="text-[var(--accent-pink)]" disabled={removingProfileId === prof.id} onClick={() => removeSocialProfile(prof.id)}>
                      {removingProfileId === prof.id ? <div className="w-2.5 h-2.5 border border-current border-t-transparent rounded-full animate-spin" /> : <Trash size={10} />}
                    </button>
                  </div>
                </div>
              ))}
              <div className="flex gap-1 mt-2">
                {platform.configured && <button className="btn-ghost text-[10px] py-1 px-2 flex-1" onClick={() => connectSocial(platform)}>{t('common.oauth')}</button>}
                <button className="btn-ghost text-[10px] py-1 px-2 flex-1" onClick={() => { setShowAddManual(platform.id); setManualName(''); }}><Plus size={10} /> {t('common.manuale')}</button>
              </div>
              {showAddManual === platform.id && (
                <div className="mt-2 p-2 rounded" style={{ background: 'var(--bg-secondary)' }}>
                  <input className="input-dark text-xs py-1.5 mb-1" placeholder={t('project.social.manualPlaceholder')} value={manualName} onChange={e => setManualName(e.target.value)} style={{ paddingLeft: '0.5rem' }} />
                  <div className="flex gap-1">
                    <button className="btn-ghost text-[10px] py-1 flex-1" onClick={() => setShowAddManual(null)}>{t('common.close')}</button>
                    <button className="btn-gradient text-[10px] py-1 flex-1" onClick={() => addManualProfile(platform.id)}>{t('common.ok')}</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Tool integrations (Google Drive etc.) */}
      {platforms.some(p => p.is_tool) && (
        <div className="mb-6">
          <p className="text-xs font-semibold text-[var(--text-muted)] uppercase mb-3">{t('project.social.integrations')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {platforms.filter(p => p.is_tool).map(platform => {
              const pi = PLATFORM_ICONS[platform.id] || { Icon: Globe, color: '#34a853' };
              const connected = socialProfiles.filter(p => p.platform === platform.id);
              const isConnected = connected.length > 0;
              return (
                <div key={platform.id} className="card" style={{ border: isConnected ? '1px solid rgba(52,168,83,0.4)' : undefined }}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(52,168,83,0.1)' }}>
                      <pi.Icon size={22} color={pi.color} />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{platform.name}</p>
                      <p className="text-[10px]" style={{ color: isConnected ? '#34a853' : 'var(--text-muted)' }}>
                        {isConnected ? t('common.connected') : t('common.notConnected')}
                      </p>
                    </div>
                  </div>
                  {isConnected ? (
                    <div className="flex gap-1">
                      <button className="btn-ghost text-[10px] py-1 px-2 flex-1" onClick={() => connectSocial(platform)}>{t('common.reconnect')}</button>
                      <button className="text-[var(--accent-pink)] text-[10px] py-1 px-2 flex-1 rounded btn-ghost" onClick={() => removeSocialProfile(connected[0].id)}>
                        <Trash size={10} className="inline mr-1" />{t('common.disconnect')}
                      </button>
                    </div>
                  ) : (
                    platform.configured && (
                      <button className="btn-gradient text-[10px] py-1.5 px-3 w-full" onClick={() => connectSocial(platform)}>
                        {t('common.connect')} {platform.name}
                      </button>
                    )
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="card"><TeamPanel projectId={project.id} /></div>
    </div>
  );
}
